<template>
  <div class="chat-page" :class="{ 'mobile-keyboard-open': isKeyboardOpen }">
    <!-- Glassmorphism Header -->
    <header class="chat-header-bar">
      <button class="back-btn" @click="$router.push('/')" aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        <span>Back</span>
      </button>
      <div class="header-title">
        <div class="header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div class="header-meta">
          <span class="header-main">Top‑up Messaging</span>
          <span v-if="activeTab === 'manager' && isOnline" class="header-status">
            <span class="status-dot"></span>{{ totalForumMembers }}  online
          </span>
          <span v-else-if="activeClient" class="header-status">
            <span class="status-dot active"></span>Active now
          </span>
        </div>
      </div>
      <div class="header-actions">
        <button class="icon-btn" @click="toggleSidebar" aria-label="Toggle sidebar" v-if="isMobile">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M3 12h18M3 6h18M3 18h18"/>
          </svg>
        </button>
      </div>
    </header>

    <!-- Main Layout -->
    <div class="chat-layout">
      <!-- Sidebar / Navigation -->
     <nav class="sidebar" :class="{
        'sidebar--compact': isCompact,
        'closed': !sidebarOpen
      }">
        <div class="sidebar-brand">
          <div class="brand-avatar">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <span class="brand-label" v-if="!isCompact">Top‑up</span>
        </div>

        <div class="sidebar-divider"></div>

        <div class="nav-group">
          <button
            class="nav-item"
            :class="{ active: activeTab === 'manager' }"
            @click="switchTab('manager')"
            aria-label="Global Forum"
          >
            <div class="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <span class="nav-label" v-if="!isCompact">Forum</span>
            <span v-if="managerUnread > 0" class="nav-badge">{{ managerUnread > 9 ? '9+' : managerUnread }}</span>
            <div class="nav-tooltip" v-if="isCompact">Global Forum</div>
          </button>

          <button
            class="nav-item"
            :class="{ active: activeTab === 'clients' }"
            @click="switchTab('clients')"
            aria-label="My Clients"
          >
            <div class="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <span class="nav-label" v-if="!isCompact">Clients</span>
            <span v-if="clientsTotalUnread > 0" class="nav-badge pulse">{{ clientsTotalUnread > 9 ? '9+' : clientsTotalUnread }}</span>
            <div class="nav-tooltip" v-if="isCompact">My Clients</div>
          </button>
        </div>

        <div class="sidebar-bottom">
          <div class="sidebar-divider"></div>
          <button class="nav-item close-item" @click="$router.push('/')" aria-label="Close chat">
            <div class="nav-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </div>
            <span class="nav-label" v-if="!isCompact">Close</span>
            <div class="nav-tooltip" v-if="isCompact">Close</div>
          </button>
        </div>
      </nav>

      <!-- Mobile Sidebar Overlay -->
      <div v-if="isMobile && sidebarOpen" class="sidebar-overlay" @click="sidebarOpen = false"></div>

      <!-- Chat Area -->
      <main class="chat-area">
        <!-- FORUM GLOBAL -->
        <section v-if="activeTab === 'manager'" class="chat-section" aria-label="Global Forum">
          <!-- Chat Header -->
          <div class="chat-header">
            <div class="chat-header-avatar forum">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div class="chat-header-info">
              <h2>Global Top‑up Forum</h2>
              <p>
                <span class="online-count">
                  <span class="pulse-dot"></span>
                  {{ totalForumMembers }} members
                </span>
              </p>
            </div>
          </div>

          <!-- Messages -->
          <div
            class="messages-container"
            ref="managerMessagesContainer"
            @scroll="handleScroll('manager')"
            :class="{ 'drag-over': dragOverTarget === 'manager' }"
            @dragover.prevent="dragOverTarget = 'manager'"
            @dragleave="dragOverTarget = null"
            @drop.prevent="handleDrop($event, 'manager')"
          >
            <!-- Skeleton Loading -->
            <div v-if="managerLoading && managerMessages.length === 0" class="skeleton-list">
              <div v-for="n in 6" :key="n" class="skeleton-message" :class="{ 'skeleton-mine': n % 2 === 0 }">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-content">
                  <div class="skeleton-line short"></div>
                  <div class="skeleton-line"></div>
                  <div class="skeleton-line medium"></div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="managerMessages.length === 0" class="empty-state">
              <div class="empty-illustration">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/>
                  <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/>
                  <path d="M9 13a3 3 0 0 0 6 0"/>
                </svg>
              </div>
              <h3>No messages yet</h3>
              <p>Be the first to start the conversation in the forum!</p>
            </div>

            <!-- Messages List -->
            <div v-else class="messages-list">
              <div
                v-for="(msg, idx) in managerMessages"
                :key="msg.id || idx"
                class="message-group"
                :class="{ 'message-group--mine': msg.is_mine }"
              >
                <div v-if="showDateSeparator(msg, idx, managerMessages)" class="date-separator">
                  <span>{{ formatDateSeparator(msg.timestamp) }}</span>
                </div>

                <div :class="['message-row', msg.is_mine ? 'mine' : 'theirs']">
                  <div class="message-avatar" :class="msg.is_mine ? 'mine' : (msg.is_manager ? 'manager' : 'topup')">
                    <svg v-if="msg.is_mine" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <svg v-else-if="msg.is_manager" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>

                  <div class="message-bubble" :class="msg.is_mine ? 'mine' : 'theirs'">
                    <div v-if="!msg.is_mine && showSenderInfo(msg, idx, managerMessages)" class="sender-info">
                      <span class="sender-name">{{ msg.sender_name }}</span>
                      <span class="sender-role" :class="msg.is_manager ? 'manager' : 'topup'">
                        {{ msg.is_manager ? 'Manager' : 'Top‑up' }}
                      </span>
                    </div>

                    <div v-if="!msg.message_type || msg.message_type === 'text'" class="message-text" v-html="formatMessage(msg.content)"></div>

                    <div v-else-if="msg.message_type === 'image'" class="message-media" @click="zoomImage(msg.file_url)">
                      <img :src="msg.file_url" @error="handleImageError" loading="lazy" />
                      <div v-if="msg.content" class="media-caption">{{ msg.content }}</div>
                    </div>

                    <div v-else-if="msg.message_type === 'file'" class="message-file" @click="openFile(msg.file_url)">
                      <div class="file-icon" :class="getFileColor(msg.file_name)">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                        </svg>
                      </div>
                      <div class="file-details">
                        <span class="file-name">{{ msg.file_name }}</span>
                        <span class="file-meta">{{ formatFileSize(msg.file_size) }}</span>
                      </div>
                      <div class="file-action">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </div>
                    </div>

                    <div class="message-meta">
                      <time>{{ formatTime(msg.timestamp) }}</time>
                      <span v-if="msg.is_mine" class="read-status" :class="{ read: msg.read }">
                        <svg v-if="msg.read" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12"/><polyline points="15 6 9 12 4 7"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Typing Indicator -->
            <div v-if="managerTyping" class="typing-indicator">
              <div class="message-avatar topup">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div class="typing-bubble">
                <span></span><span></span><span></span>
              </div>
            </div>

            <!-- Scroll to bottom -->
            <transition name="fade-up">
              <button v-if="!managerIsAtBottom && managerMessages.length" class="scroll-to-bottom" @click="scrollToBottom('manager')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 5v14M19 12l-7 7-7-7"/>
                </svg>
                <span v-if="managerNewCount" class="new-count">{{ managerNewCount }}</span>
              </button>
            </transition>

            <!-- Drag Overlay -->
            <div v-if="dragOverTarget === 'manager'" class="drag-overlay">
              <div class="drag-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Drop files here</span>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="input-area">
            <transition name="slide-up">
              <div v-if="managerSelectedFile" class="file-preview">
                <div class="preview-content">
                  <img v-if="managerPreviewType === 'image'" :src="managerPreviewUrl" class="preview-thumb" />
                  <div v-else class="preview-icon" :class="getFileColor(managerSelectedFile.name)">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div class="preview-info">
                    <span class="preview-name">{{ managerSelectedFile.name }}</span>
                    <span class="preview-size">{{ formatFileSize(managerSelectedFile.size) }}</span>
                  </div>
                </div>
                <button class="preview-remove" @click="managerClearFile" aria-label="Remove file">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </transition>

            <div class="input-wrapper">
              <button class="attach-btn" @click="$refs.managerFileInput.click()" aria-label="Attach file">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
              <div class="text-input-wrapper" :class="{ 'has-focus': inputFocus === 'manager' }">
                <textarea
                  v-model="managerDraft"
                  placeholder="Write your message..."
                  class="message-textarea"
                  @keydown.enter.prevent="handleEnter($event, 'manager')"
                  @focus="inputFocus = 'manager'"
                  @blur="inputFocus = null"
                  rows="1"
                  ref="managerTextarea"
                ></textarea>
                <button
                  class="send-btn"
                  :class="{ active: managerDraft.trim() || managerSelectedFile, sending: managerSending }"
                  @click="sendManagerMessage"
                  :disabled="managerSending || (!managerDraft.trim() && !managerSelectedFile)"
                  aria-label="Send message"
                >
                  <svg v-if="!managerSending" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  <span v-else class="spinner"></span>
                </button>
              </div>
            </div>
            <input type="file" ref="managerFileInput" style="display:none" @change="handleFileSelect($event, 'manager')" />
          </div>
        </section>

        <!-- CLIENTS TAB -->
        <section v-if="activeTab === 'clients'" class="chat-section" aria-label="My Clients">
          <!-- Contacts List -->
          <div v-if="!activeClient" class="contacts-view">
            <div class="contacts-header">
              <div class="contacts-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <span>My Clients</span>
              </div>
              <div class="contacts-actions">
                <div class="search-box">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input type="text" v-model="clientSearch" placeholder="Search clients..." />
                </div>
                <button class="whatsapp-btn" @click="openWhatsAppDialog" aria-label="Share via WhatsApp">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Share</span>
                </button>
              </div>
            </div>

            <div class="contacts-list" v-if="filteredClients.length">
              <div
                v-for="client in filteredClients"
                :key="client.downline_id"
                class="contact-card"
                :class="{ unread: client.unread_count > 0 }"
                @click="openClientChat(client)"
              >
                <div class="contact-avatar-wrapper">
                  <img :src="client.avatar || defaultAvatar" class="contact-avatar" loading="lazy" />
                  <span v-if="client.unread_count > 0" class="contact-status-dot"></span>
                </div>
                <div class="contact-info">
                  <div class="contact-header">
                    <span class="contact-name">{{ client.full_name }}</span>
                    <span v-if="client.last_message_time" class="contact-time">{{ formatTime(client.last_message_time) }}</span>
                  </div>
                  <div class="contact-preview">
                    <span class="preview-text">{{ truncateMessage(client.last_message, 55) || 'No messages yet' }}</span>
                    <span v-if="client.unread_count > 0" class="contact-unread">{{ client.unread_count > 99 ? '99+' : client.unread_count }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="empty-state compact">
              <div class="empty-illustration small">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <p>No clients found</p>
            </div>
          </div>

          <!-- Client Chat -->
          <div v-else class="chat-view">
            <div class="chat-header client-header">
              <button class="back-btn small" @click="activeClient = null" aria-label="Back to clients">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div class="chat-header-avatar client">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div class="chat-header-info">
                <h2>{{ activeClient.full_name }}</h2>
                <p>Your sponsored client</p>
              </div>
            </div>

            <div
              class="messages-container"
              ref="clientMessagesContainer"
              @scroll="handleScroll('client')"
              :class="{ 'drag-over': dragOverTarget === 'client' }"
              @dragover.prevent="dragOverTarget = 'client'"
              @dragleave="dragOverTarget = null"
              @drop.prevent="handleDrop($event, 'client')"
            >
              <div v-if="clientLoading && clientMessages.length === 0" class="skeleton-list">
                <div v-for="n in 4" :key="n" class="skeleton-message" :class="{ 'skeleton-mine': n % 2 === 0 }">
                  <div class="skeleton-avatar"></div>
                  <div class="skeleton-content">
                    <div class="skeleton-line short"></div>
                    <div class="skeleton-line"></div>
                  </div>
                </div>
              </div>

              <div v-else-if="clientMessages.length === 0" class="empty-state">
                <div class="empty-illustration">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <h3>Start a conversation</h3>
                <p>Send your first message to {{ activeClient.full_name }}</p>
              </div>

              <div v-else class="messages-list">
                <div
                  v-for="(msg, idx) in clientMessages"
                  :key="msg.id || idx"
                  class="message-group"
                  :class="{ 'message-group--mine': msg.is_mine }"
                >
                  <div v-if="showDateSeparator(msg, idx, clientMessages)" class="date-separator">
                    <span>{{ formatDateSeparator(msg.timestamp) }}</span>
                  </div>
                  <div :class="['message-row', msg.is_mine ? 'mine' : 'theirs']">
                    <div class="message-avatar" :class="msg.is_mine ? 'mine' : 'client'">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <div class="message-bubble" :class="msg.is_mine ? 'mine' : 'theirs'">
                      <div v-if="!msg.message_type || msg.message_type === 'text'" class="message-text" v-html="formatMessage(msg.content)"></div>
                      <div v-else-if="msg.message_type === 'image'" class="message-media" @click="zoomImage(msg.file_url)">
                        <img :src="msg.file_url" loading="lazy" />
                      </div>
                      <div v-else-if="msg.message_type === 'file'" class="message-file" @click="openFile(msg.file_url)">
                        <div class="file-icon" :class="getFileColor(msg.file_name)">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                          </svg>
                        </div>
                        <div class="file-details">
                          <span class="file-name">{{ msg.file_name }}</span>
                          <span class="file-meta">{{ formatFileSize(msg.file_size) }}</span>
                        </div>
                        <div class="file-action">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </div>
                      </div>
                      <div class="message-meta">
                        <time>{{ formatTime(msg.timestamp) }}</time>
                        <span v-if="msg.is_mine" class="read-status" :class="{ read: msg.read }">
                          <svg v-if="msg.read" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"/><polyline points="15 6 9 12 4 7"/>
                          </svg>
                          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <transition name="fade-up">
                <button v-if="!clientIsAtBottom && clientMessages.length" class="scroll-to-bottom" @click="scrollToBottom('client')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14M19 12l-7 7-7-7"/>
                  </svg>
                  <span v-if="clientNewCount" class="new-count">{{ clientNewCount }}</span>
                </button>
              </transition>

              <div v-if="dragOverTarget === 'client'" class="drag-overlay">
                <div class="drag-content">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Drop files here</span>
                </div>
              </div>
            </div>

            <div class="input-area">
              <transition name="slide-up">
                <div v-if="clientSelectedFile" class="file-preview">
                  <div class="preview-content">
                    <img v-if="clientPreviewType === 'image'" :src="clientPreviewUrl" class="preview-thumb" />
                    <div v-else class="preview-icon" :class="getFileColor(clientSelectedFile.name)">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <div class="preview-info">
                      <span class="preview-name">{{ clientSelectedFile.name }}</span>
                      <span class="preview-size">{{ formatFileSize(clientSelectedFile.size) }}</span>
                    </div>
                  </div>
                  <button class="preview-remove" @click="clientClearFile" aria-label="Remove file">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </transition>

              <div class="input-wrapper">
                <button class="attach-btn" @click="$refs.clientFileInput.click()" aria-label="Attach file">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <div class="text-input-wrapper" :class="{ 'has-focus': inputFocus === 'client' }">
                  <textarea
                    v-model="clientDraft"
                    :placeholder="'Message ' + activeClient.full_name + '...'"
                    class="message-textarea"
                    @keydown.enter.prevent="handleEnter($event, 'client')"
                    @focus="inputFocus = 'client'"
                    @blur="inputFocus = null"
                    rows="1"
                    ref="clientTextarea"
                  ></textarea>
                  <button
                    class="send-btn"
                    :class="{ active: clientDraft.trim() || clientSelectedFile, sending: clientSending }"
                    @click="sendClientMessage"
                    :disabled="clientSending || (!clientDraft.trim() && !clientSelectedFile)"
                    aria-label="Send message"
                  >
                    <svg v-if="!clientSending" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    <span v-else class="spinner"></span>
                  </button>
                </div>
              </div>
              <input type="file" ref="clientFileInput" style="display:none" @change="handleFileSelect($event, 'client')" />
            </div>
          </div>
        </section>
      </main>
    </div>

    <!-- Image Lightbox -->
    <transition name="fade">
      <div v-if="zoomedImage" class="image-lightbox" @click="zoomedImage = null">
        <img :src="zoomedImage" @click.stop alt="Full size image" />
        <button class="lightbox-close" @click.stop="zoomedImage = null" aria-label="Close image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <div class="lightbox-hint">Click anywhere to close</div>
      </div>
    </transition>

    <!-- WhatsApp Modal -->
    <teleport to="body">
      <transition name="modal">
        <div v-if="whatsappDialogVisible" class="modal-root" @click.self="closeWhatsAppDialog">
          <div class="modal-card modal-whatsapp">
            <div class="modal-header">
              <div class="modal-title-group">
                <div class="modal-icon whatsapp">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="modal-title">Share via WhatsApp</h3>
                  <p class="modal-subtitle">Select a client and send an invitation</p>
                </div>
              </div>
              <button class="modal-close" @click="closeWhatsAppDialog" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <div class="modal-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input v-model="whatsappSearch" type="text" placeholder="Filter clients..." />
              </div>

              <div class="client-select-list">
                <label
                  v-for="client in filteredWhatsappDownlines"
                  :key="client.downline_id"
                  class="client-select-item"
                  :class="{ selected: selectedClientId === client.downline_id }"
                >
                  <input type="radio" v-model="selectedClientId" :value="client.downline_id" />
                  <div class="select-avatar">
                    <img :src="client.avatar || defaultAvatar" loading="lazy" />
                  </div>
                  <div class="select-info">
                    <span class="select-name">{{ client.full_name }}</span>
                    <span v-if="client.phone_number" class="select-phone">{{ client.phone_number }}</span>
                    <span v-else class="select-phone missing">No phone number</span>
                  </div>
                  <div class="select-check">
                    <svg v-if="selectedClientId === client.downline_id" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                </label>
                <div v-if="filteredWhatsappDownlines.length === 0" class="empty-small">No clients found</div>
              </div>

              <div class="selection-bar">
                <span v-if="selectedClientId">1 client selected</span>
                <span v-else>Select a client above</span>
              </div>

              <div class="form-field">
                <label>Message template</label>
                <textarea v-model="whatsappMessageTemplate" rows="3" class="template-textarea"></textarea>
                <span class="field-hint">Use <code>{name}</code> for client's name, <code>{link}</code> for chat link</span>
              </div>

              <div class="preview-box">
                <label>Preview</label>
                <code>{{ whatsappPreview }}</code>
              </div>
            </div>

            <div class="modal-footer">
              <button class="btn btn-ghost" @click="closeWhatsAppDialog">Cancel</button>
              <button class="btn btn-copy" @click="copySingleLink" :disabled="!selectedClientId">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy link
              </button>
              <button class="btn btn-whatsapp" @click="sendWhatsAppToSelected" :disabled="!selectedClientId">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="toast.type"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'success'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <svg v-else-if="toast.type === 'error'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </div>
          <span class="toast-message">{{ toast.message }}</span>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TopupChatPage',
  data() {
    return {
      activeTab: 'manager',
      defaultAvatar: require('@/assets/images/avatars/1.png'),
      chatBaseUrl: 'https://test-myapp.pettycashglobal.com/?open_chat=true',

      // UI State
      isMobile: false,
      sidebarOpen: false,
      isCompact: false,
      isKeyboardOpen: false,
      inputFocus: null,
      dragOverTarget: null,
      toasts: [],
      toastId: 0,

      // Forum
      managerMessages: [],
      managerDraft: '',
      managerSelectedFile: null,
      managerPreviewUrl: null,
      managerPreviewType: null,
      managerSending: false,
      managerLoading: false,
      managerIsAtBottom: true,
      managerNewCount: 0,
      managerPollInterval: null,
      managerTyping: false,
      lastManagerFetch: 0,
      totalForumMembers: 0,

      // Clients
      downlinesList: [],
      activeClient: null,
      clientMessages: [],
      clientDraft: '',
      clientSelectedFile: null,
      clientPreviewUrl: null,
      clientPreviewType: null,
      clientSending: false,
      clientLoading: false,
      clientIsAtBottom: true,
      clientNewCount: 0,
      clientSearch: '',
      clientPollInterval: null,
      lastClientFetchMap: {},

      // Shared
      zoomedImage: null,
      imageErrors: {},
      historyFetchInterval: 5000,

      // WhatsApp
      whatsappDialogVisible: false,
      whatsappSearch: '',
      selectedClientId: null,
      whatsappMessageTemplate: "Hello {name},\n\nI would like to discuss something with you.\n\nJoin the conversation: myapp.pettycashglobal.com/?open_chat=true",
      defaultCountryCode: '237',
    }
  },
  computed: {
    managerUnread() { return 0 },
    clientsTotalUnread() {
      return this.downlinesList.reduce((s, c) => s + (c.unread_count || 0), 0)
    },
    filteredClients() {
      if (!this.clientSearch) return this.downlinesList
      const q = this.clientSearch.toLowerCase()
      return this.downlinesList.filter(c =>
        c.full_name.toLowerCase().includes(q) ||
        (c.username && c.username.toLowerCase().includes(q))
      )
    },
    filteredWhatsappDownlines() {
      if (!this.whatsappSearch) return this.downlinesList
      const q = this.whatsappSearch.toLowerCase()
      return this.downlinesList.filter(c => c.full_name.toLowerCase().includes(q))
    },
    whatsappPreview() {
      if (!this.selectedClientId) return "Select a client to see preview"
      const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId)
      if (!client) return ""
      const link = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`
      return this.whatsappMessageTemplate
        .replace(/{name}/g, client.full_name)
        .replace(/{link}/g, link)
        .substring(0, 100) + "…"
    },
    isOnline() { return navigator.onLine }
  },
  watch: {
    managerMessages: {
      handler(newVal, oldVal) {
        if (oldVal && oldVal.length > 0 && newVal.length > oldVal.length) {
          const diff = newVal.length - oldVal.length
          if (!this.managerIsAtBottom) {
            this.managerNewCount += diff
          }
        }
        this.$nextTick(() => {
          if (this.managerIsAtBottom) this.scrollToBottom('manager')
          this.autoResize('manager')
        })
      },
      deep: true
    },
    clientMessages: {
      handler(newVal, oldVal) {
        if (oldVal && oldVal.length > 0 && newVal.length > oldVal.length) {
          const diff = newVal.length - oldVal.length
          if (!this.clientIsAtBottom) {
            this.clientNewCount += diff
          }
        }
        this.$nextTick(() => {
          if (this.clientIsAtBottom) this.scrollToBottom('client')
          this.autoResize('client')
        })
      },
      deep: true
    },
    managerDraft() { this.$nextTick(() => this.autoResize('manager')) },
    clientDraft() { this.$nextTick(() => this.autoResize('client')) },
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadManagerConversation(true)
    this.loadDownlinesList()
    this.startPolling()
    window.visualViewport?.addEventListener('resize', this.handleKeyboard)
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.managerPollInterval) clearInterval(this.managerPollInterval)
    if (this.clientPollInterval) clearInterval(this.clientPollInterval)
    window.visualViewport?.removeEventListener('resize', this.handleKeyboard)
    document.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    // Responsive
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      this.isCompact = window.innerWidth <= 1024 && window.innerWidth > 768;
      if (!this.isMobile) {
        this.sidebarOpen = false; // desktop : la sidebar latérale n'est pas toggle
      } else {
        // Sur mobile, on ouvre la barre par défaut
        this.sidebarOpen = true;
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    handleKeyboard() {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height
        this.isKeyboardOpen = heightDiff > 150
        if (this.isKeyboardOpen) {
          this.$nextTick(() => this.scrollToBottom(this.activeTab))
        }
      }
    },
    handleEscape(e) {
      if (e.key === 'Escape') {
        if (this.zoomedImage) this.zoomedImage = null
        else if (this.whatsappDialogVisible) this.closeWhatsAppDialog()
        else if (this.sidebarOpen) this.sidebarOpen = false
      }
    },

    // Navigation
    switchTab(tab) {
      this.activeTab = tab
      this.activeClient = null
      this.sidebarOpen = false
      if (tab === 'manager') this.loadManagerConversation(true)
      else if (tab === 'clients') this.loadDownlinesList()
    },

    // Auto-resize textarea
    autoResize(type) {
      const el = this.$refs[`${type}Textarea`]
      if (!el) return
      el.style.height = 'auto'
      const newHeight = Math.min(el.scrollHeight, 120)
      el.style.height = newHeight + 'px'
    },
    handleEnter(e, type) {
      if (!e.shiftKey) {
        e.preventDefault()
        if (type === 'manager') this.sendManagerMessage()
        else this.sendClientMessage()
      }
    },

    // Drag & Drop
    handleDrop(e, type) {
      this.dragOverTarget = null
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.processFile(files[0], type)
      }
    },
    processFile(file, type) {
      const maxSize = file.type.startsWith('video/') ? 2000 * 1024 * 1024 : 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.showToast('File too large (max 10MB)', 'error')
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        if (type === 'manager') {
          this.managerSelectedFile = file
          this.managerPreviewUrl = e.target.result
          this.managerPreviewType = file.type.startsWith('image/') ? 'image' : 'file'
        } else {
          this.clientSelectedFile = file
          this.clientPreviewUrl = e.target.result
          this.clientPreviewType = file.type.startsWith('image/') ? 'image' : 'file'
        }
      }
      reader.readAsDataURL(file)
    },

    // Toast system
    showToast(message, type = 'info', duration = 3000) {
      const id = ++this.toastId
      this.toasts.push({ id, message, type })
      setTimeout(() => {
        const idx = this.toasts.findIndex(t => t.id === id)
        if (idx !== -1) this.toasts.splice(idx, 1)
      }, duration)
    },

    // Forum
    async loadManagerConversation(markRead = true, isBackground = false) {
      if (this.managerLoading && !isBackground) return
      const now = Date.now()
      if (now - this.lastManagerFetch < this.historyFetchInterval && !markRead) return
      this.lastManagerFetch = now
      if (!isBackground) this.managerLoading = true
      try {
        const res = await axios.get(`/api/v1/manager/forum/messages/?mark_read=${markRead ? 'true' : 'false'}`)
        if (res.data.messages) {
          this.managerMessages = res.data.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          this.totalForumMembers = res.data.total_members || 0
         if (!isBackground) this.$nextTick(() => this.scrollToBottom('manager', true))
        }
      } catch (e) {
        console.error(e)
        if (!isBackground) this.showToast('Failed to load messages', 'error')
      } finally {
        if (!isBackground) this.managerLoading = false
      }
    },
    async sendManagerMessage() {
      if ((!this.managerDraft.trim() && !this.managerSelectedFile) || this.managerSending) return
      this.managerSending = true
      const fd = new FormData()
      fd.append('message', this.managerDraft.trim())
      fd.append('message_type', this.managerSelectedFile ? this.getFileType(this.managerSelectedFile) : 'text')
      if (this.managerSelectedFile) fd.append('file', this.managerSelectedFile)
      try {
        await axios.post('/api/v1/manager/forum/send/', fd)
        this.managerDraft = ''
        this.managerClearFile()
        await this.loadManagerConversation(true)
        this.showToast('Message sent', 'success')
      } catch (e) {
        this.showToast('Failed to send message', 'error')
      } finally {
        this.managerSending = false
      }
    },
    managerClearFile() {
      this.managerSelectedFile = null
      this.managerPreviewUrl = null
      this.managerPreviewType = null
    },

    // Clients
    async loadDownlinesList() {
      try {
        const res = await axios.get('/api/v1/topup/my_downlines/')
        this.downlinesList = (res.data.downlines || []).map(d => ({ ...d, phone_number: d.phone_number || '' }))
      } catch (e) {
        console.error(e)
        this.showToast('Failed to load clients', 'error')
      }
    },
    async openClientChat(client) {
      this.activeClient = client
      this.clientMessages = []
      await this.loadClientMessages(client.downline_id, true)
    },
    async loadClientMessages(downlineId, markRead = true, isBackground = false) {
      const key = `client_${downlineId}`
      const lastFetch = this.lastClientFetchMap[key] || 0
      if (Date.now() - lastFetch < this.historyFetchInterval && !markRead) return
      this.lastClientFetchMap[key] = Date.now()
      if (!isBackground) this.clientLoading = true
      try {
        const res = await axios.get(`/api/v1/topup/conversation_with_downline/${downlineId}/?mark_read=${markRead ? 'true' : 'false'}`)
        if (res.data.messages) {
          this.clientMessages = res.data.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          if (markRead) {
            const idx = this.downlinesList.findIndex(c => c.downline_id === downlineId)
            if (idx !== -1) this.downlinesList[idx].unread_count = 0
          }
         if (!isBackground) this.$nextTick(() => this.scrollToBottom('client', true))
        }
      } catch (e) {
        console.error(e)
        if (!isBackground) this.showToast('Failed to load conversation', 'error')
      } finally {
        if (!isBackground) this.clientLoading = false
      }
    },
    async sendClientMessage() {
      if ((!this.clientDraft.trim() && !this.clientSelectedFile) || this.clientSending || !this.activeClient) return
      this.clientSending = true
      const fd = new FormData()
      fd.append('downline_id', this.activeClient.downline_id)
      fd.append('message', this.clientDraft.trim())
      fd.append('message_type', this.clientSelectedFile ? this.getFileType(this.clientSelectedFile) : 'text')
      if (this.clientSelectedFile) fd.append('file', this.clientSelectedFile)
      try {
        await axios.post('/api/v1/topup/send_to_downline/', fd)
        this.clientDraft = ''
        this.clientClearFile()
        await this.loadClientMessages(this.activeClient.downline_id, true)
        this.showToast('Message sent', 'success')
      } catch (e) {
        this.showToast('Failed to send message', 'error')
      } finally {
        this.clientSending = false
      }
    },
    clientClearFile() {
      this.clientSelectedFile = null
      this.clientPreviewUrl = null
      this.clientPreviewType = null
    },

    // Shared
    handleFileSelect(event, origin) {
      const file = event.target.files[0]
      if (!file) return
      this.processFile(file, origin)
      event.target.value = ''
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image'
      if (file.type.startsWith('video/')) return 'video'
      return 'file'
    },
    getFileIcon(name) {
      if (!name) return 'fas fa-file'
      const ext = name.split('.').pop().toLowerCase()
      const icons = {
        pdf: 'fas fa-file-pdf', doc: 'fas fa-file-word', docx: 'fas fa-file-word',
        jpg: 'fas fa-file-image', jpeg: 'fas fa-file-image', png: 'fas fa-file-image',
        zip: 'fas fa-file-archive', rar: 'fas fa-file-archive', mp4: 'fas fa-file-video'
      }
      return icons[ext] || 'fas fa-file'
    },
    getFileColor(name) {
      if (!name) return 'default'
      const ext = name.split('.').pop().toLowerCase()
      const colors = {
        pdf: 'red', doc: 'blue', docx: 'blue', jpg: 'purple', jpeg: 'purple',
        png: 'purple', zip: 'orange', mp4: 'pink'
      }
      return colors[ext] || 'default'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i]
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(ts)
      const now = new Date()
      if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    formatMessage(text) {
      if (!text) return ''
      return text
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
        .replace(/\n/g, '<br>')
    },
    showDateSeparator(msg, idx, list) {
      if (idx === 0) return true
      const prev = list[idx - 1]
      return new Date(prev.timestamp).toDateString() !== new Date(msg.timestamp).toDateString()
    },
    showSenderInfo(msg, idx, list) {
      if (idx === 0) return true
      const prev = list[idx - 1]
      return prev.sender_name !== msg.sender_name ||
        new Date(prev.timestamp).toDateString() !== new Date(msg.timestamp).toDateString()
    },
    formatDateSeparator(ts) {
      const d = new Date(ts)
      const today = new Date().toDateString()
      const yesterday = new Date(Date.now() - 86400000).toDateString()
      if (d.toDateString() === today) return 'Today'
      if (d.toDateString() === yesterday) return 'Yesterday'
      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
    },
    scrollToBottom(type, instant = false) {
      const el = this.$refs[`${type}MessagesContainer`]
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'instant' : 'smooth' })
        this[`${type}IsAtBottom`] = true
        this[`${type}NewCount`] = 0
      }
    },
    handleScroll(type) {
      const el = this.$refs[`${type}MessagesContainer`]
      if (!el) return
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
      this[`${type}IsAtBottom`] = atBottom
      if (atBottom) this[`${type}NewCount`] = 0
    },
    zoomImage(url) { this.zoomedImage = url },
    handleImageError() {},
    openFile(url) { window.open(url, '_blank') },
    startPolling() {
      this.managerPollInterval = setInterval(() => {
        if (this.activeTab === 'manager') this.loadManagerConversation(false, true)
      }, 8000)
      this.clientPollInterval = setInterval(() => {
        if (this.activeTab === 'clients') {
          this.loadDownlinesList()
          if (this.activeClient) this.loadClientMessages(this.activeClient.downline_id, false, true)
        }
      }, 8000)
    },
    truncateMessage(text, len = 50) {
      return text?.length > len ? text.substring(0, len) + '…' : text || ''
    },

    // WhatsApp
    openWhatsAppDialog() {
      this.selectedClientId = null
      this.whatsappSearch = ''
      this.whatsappDialogVisible = true
    },
    closeWhatsAppDialog() {
      this.whatsappDialogVisible = false
      this.selectedClientId = null
      this.whatsappSearch = ''
    },
    formatPhoneForWhatsApp(rawPhone) {
      if (!rawPhone) return ''
      let digits = rawPhone.toString().replace(/\D/g, '')
      if (digits.startsWith('0')) digits = digits.substring(1)
      if (digits.length === 9) digits = this.defaultCountryCode + digits
      return digits ? `+${digits}` : ''
    },
    async sendWhatsAppToSelected() {
      if (!this.selectedClientId) return this.showToast('Select a client', 'error')
      const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId)
      if (!client) return
      const phone = this.formatPhoneForWhatsApp(client.phone_number)
      const link = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`
      const msg = this.whatsappMessageTemplate.replace(/{name}/g, client.full_name).replace(/{link}/g, link)
      const url = phone
        ? `https://wa.me/${encodeURIComponent(phone)}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`
      window.open(url, '_blank')
      this.showToast(`WhatsApp opened for ${client.full_name}`, 'success')
      this.closeWhatsAppDialog()
    },
    async copySingleLink() {
      if (!this.selectedClientId) return this.showToast('Select a client', 'error')
      const client = this.downlinesList.find(c => c.downline_id === this.selectedClientId)
      if (!client) return
      const link = `${this.chatBaseUrl}/chat/chat/${client.downline_id}`
      await navigator.clipboard.writeText(link)
      this.showToast('Link copied to clipboard', 'success')
    }
  }
}
</script>
<style>
html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

/* Masque le footer PettyCash sur cette page */
footer,
.footer,
.site-footer,
[class*="footer"],
[class*="copyright"] {
  display: none !important;
}
</style>

<style scoped>
.chat-page {
  position: fixed;        /* ← Prend tout le viewport, ignore header/footer parent */
  inset: 0;               /* top:0; right:0; bottom:0; left:0 */
  z-index: 100;
  display: flex;
  flex-direction: column;
  background: #0b1120;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #f8fafc;
}

/* ===== Glassmorphism Header ===== */
.chat-header-bar {
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(20px) saturate(1.5);
  -webkit-backdrop-filter: blur(20px) saturate(1.5);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  z-index: 50;
}
.back-btn {
  background: transparent;
  border: none;
  color: #f59e0b;
  font-size: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 9999px;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.back-btn:hover { background: rgba(245, 158, 11, 0.1); transform: translateX(-2px); }
.back-btn svg { flex-shrink: 0; }
.header-title { display: flex; align-items: center; gap: 12px; flex: 1; }
.header-icon {
  width: 40px; height: 40px; border-radius: 16px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex; align-items: center; justify-content: center;
  color: white; box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}
.header-meta { display: flex; flex-direction: column; gap: 2px; }
.header-main { font-size: 17px; font-weight: 700; color: #f8fafc; letter-spacing: -0.01em; }
.header-status { font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 6px; }
.status-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #10b981; box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
  animation: pulse-dot 2s infinite;
}
.status-dot.active { background: #f59e0b; box-shadow: 0 0 8px rgba(245, 158, 11, 0.5); }
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.8); }
}
.icon-btn {
  width: 40px; height: 40px; border-radius: 50%;
  background: #1e293b; border: 1px solid #2d3a4f; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.icon-btn:hover { background: #243447; color: #f8fafc; }

/* ===== CHAÎNE FLEX CRITIQUE ===== */
.chat-layout {
  flex: 1;
  min-height: 0;        /* CRITIQUE */
  display: flex;
  flex-direction: row;
  overflow: hidden;
}
.chat-area {
  flex: 1;
  min-height: 0;        /* CRITIQUE */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.chat-section {
  flex: 1;
  min-height: 0;        /* CRITIQUE */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.contacts-view,
.chat-view {
  flex: 1;
  min-height: 0;        /* CRITIQUE */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.messages-container {
  flex: 1;
  min-height: 0;        /* CRITIQUE */
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: linear-gradient(180deg, #0b1120 0%, #151e32 100%);
  position: relative;
  scroll-behavior: smooth;
}
/* La barre de saisie NE RÉTRÉCIT JAMAIS */
.input-area {
  flex: 0 0 auto;       /* CRITIQUE : taille fixe, ne rétrécit pas */
  flex-shrink: 0;
  padding: 8px 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  background: #151e32;
  max-height: 300px;
  overflow-y: auto;
}

/* ===== Sidebar ===== */
.sidebar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 56px;
  width: 100%;
  flex-direction: row;
  justify-content: center;
  padding: 0 12px;
  border-right: none;
  border-top: 1px solid rgba(148,163,184,0.1);
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(20px);
  z-index: 50;

  /* Ajoute une transition pour le slide */
  transform: translateY(0);
  transition: transform 0.3s ease;
}

/* Quand la sidebar est fermée, on la descend en dehors de l'écran */
.sidebar.closed {
  transform: translateY(100%);
}
.sidebar--compact { width: 72px; }
.sidebar-brand { display: flex; flex-direction: column; align-items: center; gap: 8px; margin-bottom: 8px; }
.brand-avatar {
  width: 48px; height: 48px; border-radius: 16px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex; align-items: center; justify-content: center;
  color: white; box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}
.brand-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.sidebar-divider { width: 32px; height: 1px; background: #2d3a4f; margin: 8px 0; }
.nav-group { display: flex; flex-direction: column; gap: 8px; width: 100%; padding: 0 12px; }
.nav-item {
  width: 100%; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; border: none; background: transparent;
  color: #64748b; position: relative;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.nav-item:hover { background: #243447; color: #94a3b8; }
.nav-item.active {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white; box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}
.nav-item.active .nav-icon { color: white; }
.nav-icon { display: flex; align-items: center; justify-content: center; transition: 0.15s; }
.nav-label { display: none; }
.nav-badge {
  position: absolute; top: 4px; right: 4px;
  background: #ef4444; color: white; font-size: 10px; font-weight: 700;
  min-width: 18px; height: 18px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #151e32;
}
.nav-badge.pulse { animation: badge-pulse 2s infinite; }
@keyframes badge-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0); }
}
.nav-tooltip {
  position: absolute; left: calc(100% + 12px); top: 50%; transform: translateY(-50%);
  background: #1e293b; color: #f8fafc; padding: 6px 12px; border-radius: 8px;
  font-size: 12px; font-weight: 500; white-space: nowrap;
  border: 1px solid #2d3a4f; opacity: 0; pointer-events: none;
  transition: 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
.nav-item:hover .nav-tooltip { opacity: 1; }
.sidebar-bottom { margin-top: auto; display: flex; flex-direction: column; gap: 8px; width: 100%; padding: 0 12px; }
.close-item:hover { background: rgba(239, 68, 68, 0.1); color: #ef4444; }
.sidebar-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px); z-index: 35;
}

/* ===== Chat Headers ===== */
.chat-header {
  padding: 14px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  display: flex; align-items: center; gap: 14px;
  flex-shrink: 0; background: #151e32;
}
.chat-header-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.chat-header-avatar.forum {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white; box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}
.chat-header-avatar.client {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}
.chat-header-info h2 { font-size: 16px; font-weight: 700; color: #f8fafc; margin: 0; }
.chat-header-info p { font-size: 12px; color: #64748b; margin: 2px 0 0; display: flex; align-items: center; gap: 6px; }
.online-count { display: flex; align-items: center; gap: 6px; }
.pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #10b981; animation: pulse-dot 2s infinite; }

/* ===== Scrollbar ===== */
.messages-container::-webkit-scrollbar { width: 6px; }
.messages-container::-webkit-scrollbar-track { background: transparent; }
.messages-container::-webkit-scrollbar-thumb { background: #2d3a4f; border-radius: 3px; }
.messages-container.drag-over { background: rgba(245, 158, 11, 0.05); }

/* ===== Skeleton ===== */
.skeleton-list { display: flex; flex-direction: column; gap: 16px; padding: 8px 0; }
.skeleton-message { display: flex; gap: 12px; align-items: flex-end; animation: skeleton-pulse 1.5s ease-in-out infinite; }
.skeleton-message.skeleton-mine { flex-direction: row-reverse; }
.skeleton-avatar { width: 32px; height: 32px; border-radius: 50%; background: #2d3a4f; flex-shrink: 0; }
.skeleton-content { display: flex; flex-direction: column; gap: 6px; max-width: 60%; }
.skeleton-line { height: 12px; border-radius: 6px; background: #2d3a4f; width: 100%; }
.skeleton-line.short { width: 40%; height: 10px; }
.skeleton-line.medium { width: 70%; }
@keyframes skeleton-pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

/* ===== Empty State ===== */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 60px 20px; text-align: center; color: #64748b;
}
.empty-state.compact { padding: 40px 20px; }
.empty-illustration {
  width: 100px; height: 100px; border-radius: 50%; background: #151e32;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 20px; color: #2d3a4f;
}
.empty-illustration.small { width: 64px; height: 64px; margin-bottom: 12px; }
.empty-illustration svg { opacity: 0.5; }
.empty-state h3 { font-size: 18px; font-weight: 700; color: #94a3b8; margin: 0 0 8px; }
.empty-state p { font-size: 14px; color: #64748b; margin: 0; max-width: 280px; line-height: 1.5; }

/* ===== Messages ===== */
.messages-list { display: flex; flex-direction: column; gap: 2px; }
.message-group { display: flex; flex-direction: column; gap: 2px; animation: message-appear 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes message-appear { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.message-row { display: flex; gap: 10px; align-items: flex-end; padding: 2px 0; }
.message-row.mine { flex-direction: row-reverse; }
.message-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: white; font-size: 12px;
}
.message-avatar.manager { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.message-avatar.topup { background: linear-gradient(135deg, #10b981, #059669); }
.message-avatar.mine { background: linear-gradient(135deg, #475569, #334155); }
.message-avatar.client { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.message-bubble { max-width: 70%; padding: 10px 14px; border-radius: 18px; position: relative; word-wrap: break-word; }
.message-bubble.mine { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; border-bottom-right-radius: 4px; }
.message-bubble.theirs { background: #1e293b; color: #f8fafc; border: 1px solid rgba(148, 163, 184, 0.1); border-bottom-left-radius: 4px; }
.sender-info { margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
.sender-name { font-weight: 600; font-size: 13px; color: #f8fafc; }
.sender-role { font-size: 10px; padding: 2px 8px; border-radius: 9999px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; }
.sender-role.manager { background: rgba(59, 130, 246, 0.2); color: #60a5fa; }
.sender-role.topup { background: rgba(16, 185, 129, 0.2); color: #34d399; }
.message-text { font-size: 14px; line-height: 1.5; }
.message-text a { color: #fbbf24; text-decoration: underline; text-underline-offset: 2px; }
.message-bubble.theirs .message-text a { color: #f59e0b; }
.message-media { cursor: pointer; border-radius: 16px; overflow: hidden; margin: 4px 0; transition: 0.2s; }
.message-media:hover { transform: scale(1.02); }
.message-media img { max-width: 240px; max-height: 200px; border-radius: 16px; display: block; object-fit: cover; }
.media-caption { font-size: 12px; color: #94a3b8; margin-top: 6px; padding: 0 2px; }
.message-file { display: flex; align-items: center; gap: 12px; padding: 10px 12px; background: rgba(0,0,0,0.2); border-radius: 16px; cursor: pointer; transition: 0.2s; }
.message-file:hover { background: rgba(0,0,0,0.3); transform: translateX(2px); }
.message-bubble.mine .message-file { background: rgba(255,255,255,0.15); }
.message-bubble.mine .message-file:hover { background: rgba(255,255,255,0.25); }
.file-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: white; }
.file-icon.red { background: linear-gradient(135deg, #ef4444, #dc2626); }
.file-icon.blue { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.file-icon.purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.file-icon.orange { background: linear-gradient(135deg, #f97316, #ea580c); }
.file-icon.pink { background: linear-gradient(135deg, #ec4899, #db2777); }
.file-icon.default { background: linear-gradient(135deg, #64748b, #475569); }
.file-details { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.file-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-meta { font-size: 11px; opacity: 0.7; }
.file-action { opacity: 0.6; transition: 0.15s; }
.message-file:hover .file-action { opacity: 1; }
.message-meta { display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: 6px; font-size: 11px; opacity: 0.7; }
.message-bubble.mine .message-meta { color: rgba(255,255,255,0.8); }
.read-status { display: flex; align-items: center; }
.read-status.read { color: #60a5fa; }

/* ===== Date Separator ===== */
.date-separator { text-align: center; margin: 16px 0; position: relative; }
.date-separator::before { content: ''; position: absolute; top: 50%; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, #2d3a4f, transparent); }
.date-separator span { position: relative; background: #0b1120; padding: 4px 16px; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; border-radius: 9999px; border: 1px solid #2d3a4f; }

/* ===== Typing ===== */
.typing-indicator { display: flex; align-items: flex-end; gap: 10px; padding: 8px 0; animation: message-appear 0.3s ease; }
.typing-bubble { background: #1e293b; border: 1px solid rgba(148,163,184,0.1); border-radius: 18px; border-bottom-left-radius: 4px; padding: 14px 18px; display: flex; gap: 4px; align-items: center; }
.typing-bubble span { width: 8px; height: 8px; border-radius: 50%; background: #64748b; animation: typing-bounce 1.4s infinite ease-in-out both; }
.typing-bubble span:nth-child(1) { animation-delay: -0.32s; }
.typing-bubble span:nth-child(2) { animation-delay: -0.16s; }
@keyframes typing-bounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }

/* ===== Scroll to Bottom ===== */
.scroll-to-bottom {
  position: absolute; bottom: 20px; right: 24px;
  background: #f59e0b; color: white; border: none; border-radius: 9999px;
  padding: 10px 16px; display: flex; align-items: center; gap: 8px;
  cursor: pointer; font-weight: 600; font-size: 13px; z-index: 10;
  transition: 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.4), 0 0 0 4px rgba(245,158,11,0.15);
}
.scroll-to-bottom:hover { transform: translateY(-2px) scale(1.05); }
.new-count { background: white; color: #d97706; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px; }

/* ===== Drag Overlay ===== */
.drag-overlay { position: absolute; inset: 0; background: rgba(245,158,11,0.1); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; z-index: 20; border: 2px dashed #f59e0b; border-radius: 24px; margin: 12px; }
.drag-content { display: flex; flex-direction: column; align-items: center; gap: 12px; color: #f59e0b; font-weight: 600; font-size: 16px; }

/* ===== Input internals ===== */
.file-preview { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: #0b1120; border-radius: 16px; margin-bottom: 10px; border: 1px solid #2d3a4f; border-left: 3px solid #f59e0b; }
.preview-content { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.preview-thumb { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; }
.preview-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; }
.preview-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.preview-name { font-size: 13px; font-weight: 600; color: #f8fafc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.preview-size { font-size: 11px; color: #64748b; }
.preview-remove { width: 32px; height: 32px; border-radius: 50%; background: transparent; border: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.15s; flex-shrink: 0; }
.preview-remove:hover { background: rgba(239,68,68,0.1); color: #ef4444; }
.input-wrapper { display: flex; align-items: flex-end; gap: 10px; }
.attach-btn { width: 44px; height: 44px; border-radius: 50%; background: #1e293b; border: 1px solid #2d3a4f; color: #94a3b8; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: 0.2s; }
.attach-btn:hover { background: #243447; color: #f8fafc; border-color: #f59e0b; }
.text-input-wrapper { flex: 1; display: flex; align-items: flex-end; background: #0b1120; border-radius: 24px; border: 1px solid #2d3a4f; padding: 8px 8px 8px 16px; transition: 0.2s; }
.text-input-wrapper.has-focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.2); }
.message-textarea { flex: 1; background: transparent; border: none; outline: none; color: #f8fafc; font-size: 15px; line-height: 1.5; padding: 6px 0; resize: none; max-height: 120px; font-family: inherit; }
.message-textarea::placeholder { color: #64748b; }
.send-btn { width: 40px; height: 40px; border-radius: 50%; background: #2d3a4f; color: #64748b; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; margin-left: 4px; transition: 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.send-btn.active { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; box-shadow: 0 4px 12px rgba(245,158,11,0.3); }
.send-btn.active:hover { transform: scale(1.1); }
.send-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }
.spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== Contacts ===== */
.contacts-header { padding: 16px 20px; border-bottom: 1px solid rgba(148,163,184,0.1); display: flex; align-items: center; gap: 16px; flex-wrap: wrap; background: #151e32; flex-shrink: 0; }
.contacts-title { display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 700; color: #f8fafc; }
.contacts-actions { display: flex; align-items: center; gap: 12px; flex: 1; justify-content: flex-end; }
.search-box { display: flex; align-items: center; gap: 10px; background: #0b1120; border: 1px solid #2d3a4f; border-radius: 9999px; padding: 8px 14px; flex: 1; max-width: 280px; transition: 0.2s; }
.search-box:focus-within { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.2); }
.search-box svg { color: #64748b; flex-shrink: 0; }
.search-box input { flex: 1; background: transparent; border: none; outline: none; color: #f8fafc; font-size: 14px; font-family: inherit; }
.search-box input::placeholder { color: #64748b; }
.whatsapp-btn { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #25D366, #128C7E); color: white; border: none; border-radius: 9999px; padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer; transition: 0.2s; box-shadow: 0 4px 12px rgba(37,211,102,0.3); flex-shrink: 0; }
.whatsapp-btn:hover { transform: translateY(-2px); }
.contacts-list { flex: 1; min-height: 0; overflow-y: auto; padding: 8px; }
.contact-card { display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-radius: 16px; cursor: pointer; transition: 0.2s; border: 1px solid transparent; }
.contact-card:hover { background: #1e293b; border-color: rgba(148,163,184,0.1); transform: translateX(4px); }
.contact-card.unread { background: rgba(245,158,11,0.05); border-left: 3px solid #f59e0b; }
.contact-card.unread:hover { background: rgba(245,158,11,0.08); }
.contact-avatar-wrapper { position: relative; flex-shrink: 0; }
.contact-avatar { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; border: 2px solid #2d3a4f; transition: 0.2s; }
.contact-card:hover .contact-avatar { border-color: #f59e0b; }
.contact-status-dot { position: absolute; bottom: 2px; right: 2px; width: 14px; height: 14px; border-radius: 50%; background: #f59e0b; border: 2px solid #0b1120; animation: pulse-dot 2s infinite; }
.contact-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.contact-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.contact-name { font-weight: 700; font-size: 15px; color: #f8fafc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.contact-time { font-size: 11px; color: #64748b; flex-shrink: 0; }
.contact-preview { display: flex; align-items: center; gap: 8px; }
.preview-text { font-size: 13px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
.contact-unread { background: #f59e0b; color: #0b1120; border-radius: 9999px; padding: 2px 10px; font-size: 12px; font-weight: 700; flex-shrink: 0; }

/* ===== Lightbox ===== */
.image-lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.92); backdrop-filter: blur(10px); display: flex; align-items: center; justify-content: center; z-index: 2000; cursor: zoom-out; }
.image-lightbox img { max-width: 90vw; max-height: 85vh; border-radius: 16px; cursor: default; }
.lightbox-close { position: absolute; top: 24px; right: 24px; width: 48px; height: 48px; border-radius: 50%; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
.lightbox-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
.lightbox-hint { position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%); color: rgba(255,255,255,0.5); font-size: 13px; pointer-events: none; }

/* ===== Modal ===== */
.modal-root { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 20px; }
.modal-card { background: #151e32; border-radius: 24px; width: 100%; max-width: 520px; max-height: 85vh; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #2d3a4f; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: #0b1120; border-bottom: 1px solid rgba(148,163,184,0.1); flex-shrink: 0; }
.modal-title-group { display: flex; align-items: center; gap: 14px; }
.modal-icon { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.modal-icon.whatsapp { background: linear-gradient(135deg, #25D366, #128C7E); color: white; }
.modal-title { font-size: 18px; font-weight: 700; color: #f8fafc; margin: 0; }
.modal-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }
.modal-close { width: 36px; height: 36px; border-radius: 50%; background: transparent; border: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.15s; }
.modal-close:hover { background: #1e293b; color: #f8fafc; transform: rotate(90deg); }
.modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
.modal-search { display: flex; align-items: center; gap: 10px; background: #0b1120; border: 1px solid #2d3a4f; border-radius: 9999px; padding: 10px 16px; margin-bottom: 16px; }
.modal-search svg { color: #64748b; flex-shrink: 0; }
.modal-search input { flex: 1; background: transparent; border: none; outline: none; color: #f8fafc; font-size: 14px; font-family: inherit; }
.client-select-list { max-height: 240px; overflow-y: auto; border: 1px solid #2d3a4f; border-radius: 16px; background: #0b1120; margin-bottom: 16px; }
.client-select-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; cursor: pointer; transition: 0.15s; border-bottom: 1px solid rgba(148,163,184,0.1); }
.client-select-item:last-child { border-bottom: none; }
.client-select-item:hover, .client-select-item.selected { background: #1e293b; }
.client-select-item input[type="radio"] { accent-color: #f59e0b; width: 18px; height: 18px; cursor: pointer; flex-shrink: 0; }
.select-avatar { width: 36px; height: 36px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.select-avatar img { width: 100%; height: 100%; object-fit: cover; }
.select-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.select-name { font-weight: 600; font-size: 14px; color: #f8fafc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.select-phone { font-size: 12px; color: #64748b; background: #1e293b; padding: 2px 8px; border-radius: 9999px; display: inline-block; width: fit-content; }
.select-phone.missing { background: rgba(239,68,68,0.15); color: #fca5a5; }
.select-check { width: 24px; height: 24px; color: #f59e0b; display: flex; align-items: center; justify-content: center; }
.selection-bar { font-size: 13px; color: #94a3b8; background: #0b1120; padding: 10px 14px; border-radius: 9999px; text-align: center; margin-bottom: 16px; border: 1px solid #2d3a4f; }
.form-field { margin-bottom: 16px; }
.form-field label { display: block; margin-bottom: 8px; font-weight: 600; font-size: 13px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.template-textarea { width: 100%; background: #0b1120; border: 1px solid #2d3a4f; border-radius: 16px; padding: 12px 16px; color: #f8fafc; font-family: inherit; font-size: 14px; line-height: 1.5; resize: vertical; transition: 0.2s; }
.template-textarea:focus { outline: none; border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.2); }
.field-hint { display: block; font-size: 11px; color: #64748b; margin-top: 6px; }
.field-hint code { background: #1e293b; padding: 2px 6px; border-radius: 4px; color: #f59e0b; font-size: 11px; }
.preview-box { background: #0b1120; border: 1px solid #2d3a4f; border-radius: 16px; padding: 14px; }
.preview-box label { display: block; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.preview-box code { font-size: 12px; word-break: break-all; color: #94a3b8; line-height: 1.5; display: block; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; background: #0b1120; border-top: 1px solid rgba(148,163,184,0.1); flex-shrink: 0; }
.btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 9999px; font-size: 14px; font-weight: 600; cursor: pointer; transition: 0.2s; border: none; font-family: inherit; }
.btn-ghost { background: transparent; border: 1px solid #2d3a4f; color: #94a3b8; }
.btn-ghost:hover { background: #1e293b; color: #f8fafc; }
.btn-copy { background: #1e293b; color: #f8fafc; border: 1px solid #2d3a4f; }
.btn-copy:hover { background: #243447; }
.btn-whatsapp { background: linear-gradient(135deg, #25D366, #128C7E); color: white; box-shadow: 0 4px 12px rgba(37,211,102,0.3); }
.btn-whatsapp:hover:not(:disabled) { transform: translateY(-2px); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

/* ===== Toasts ===== */
.toast-container { position: fixed; top: 20px; right: 20px; z-index: 99999; display: flex; flex-direction: column; gap: 10px; pointer-events: none; }
.toast { display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: 16px; background: #1e293b; border: 1px solid #2d3a4f; color: #f8fafc; font-size: 14px; font-weight: 500; pointer-events: auto; max-width: 320px; }
.toast.success { border-left: 3px solid #10b981; }
.toast.error { border-left: 3px solid #ef4444; }
.toast.info { border-left: 3px solid #3b82f6; }
.toast-icon { flex-shrink: 0; }
.toast.success .toast-icon { color: #10b981; }
.toast.error .toast-icon { color: #ef4444; }
.toast.info .toast-icon { color: #3b82f6; }

/* ===== Transitions ===== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.fade-up-enter-active { animation: fade-up-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.fade-up-leave-active { animation: fade-up-out 0.2s ease forwards; }
@keyframes fade-up-in { from { opacity: 0; transform: translateY(10px) scale(0.9); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes fade-up-out { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(10px) scale(0.9); } }
.slide-up-enter-active { animation: slide-up-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.slide-up-leave-active { animation: slide-up-out 0.2s ease forwards; }
@keyframes slide-up-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slide-up-out { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-10px); } }
.modal-enter-active { animation: modal-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.modal-leave-active { animation: modal-out 0.2s ease forwards; }
@keyframes modal-in { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes modal-out { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.95); } }
.toast-enter-active { animation: toast-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.toast-leave-active { animation: toast-out 0.3s ease forwards; }
@keyframes toast-in { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
@keyframes toast-out { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(30px); } }

/* ===== Mobile ===== */
@media (max-width: 768px) {
  .chat-page.mobile-keyboard-open .chat-header-bar { display: none; }
  .sidebar {
    position: fixed; bottom: 0; left: 0; right: 0;
    height: 56px; width: 100%; flex-shrink: 0;
    flex-direction: row; justify-content: center;
    padding: 0 12px; border-right: none;
    border-top: 1px solid rgba(148,163,184,0.1);
    background: rgba(15, 23, 42, 0.95);
    backdrop-filter: blur(20px); z-index: 50;
  }
  .sidebar-brand, .sidebar-divider, .sidebar-bottom, .nav-tooltip { display: none; }
  .nav-group { flex-direction: row; gap: 0; width: auto; padding: 0; }
  .nav-item { width: 64px; height: 48px; border-radius: 16px; }
  .chat-layout { flex-direction: column; padding-bottom: 56px; }
  .message-bubble { max-width: 85%; }
  .contacts-header { padding: 12px 16px; }
  .whatsapp-btn span { display: none; }
  .modal-root { padding: 12px; }
  .modal-footer { flex-wrap: wrap; }
  .modal-footer .btn { flex: 1; justify-content: center; }
  .toast-container { left: 16px; right: 16px; top: 16px; }
  .toast { max-width: 100%; }
}

@media (max-width: 480px) {
  .chat-header-bar { padding: 10px 14px; }
  .header-main { font-size: 15px; }
  .message-bubble { max-width: 90%; padding: 8px 12px; }
  .message-text { font-size: 13px; }
  .input-area { padding: 8px 12px; }
  .message-textarea { font-size: 14px; }
  .contact-card { padding: 10px 12px; }
  .contact-avatar { width: 44px; height: 44px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
  .messages-container { scroll-behavior: auto; }
}
</style>
